import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Tag, Users, Headphones } from 'lucide-react';
import SignInComponent, { Testimonial } from './SignInComponent';
import Navbar from './components/ui/navbar';

const sampleTestimonials: Testimonial[] = [
  {
    avatarSrc: "https://randomuser.me/api/portraits/women/57.jpg",
    name: "Sarah Chen",
    handle: "@sarahdigital",
    text: "Placeholder for now: Amazing platform! The user experience is seamless and the features are exactly what I needed."
  },
  {
    avatarSrc: "https://randomuser.me/api/portraits/men/64.jpg",
    name: "Marcus Johnson",
    handle: "@marcustech",
    text: "Placeholder for now: This service has transformed how I work. Clean design, powerful features, and excellent support."
  },
  {
    avatarSrc: "https://randomuser.me/api/portraits/men/32.jpg",
    name: "David Martinez",
    handle: "@davidcreates",
    text: "I've tried many platforms, but this one stands out. Intuitive, reliable, and genuinely helpful for productivity."
  },
];

const SignInDemo: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [heroImage] = useState(() => {
    // Randomly select between loginimage.jpg and loginimage2.jpg
    const randomNum = Math.random();
    return randomNum < 0.5 ? '/loginimage.jpg' : '/loginimage2.jpg';
  });

  useEffect(() => {
    // Simulate component loading time
    const timer = setTimeout(() => {
      setLoading(false);
    }, 300); // Short loading time for sign-in page

    return () => clearTimeout(timer);
  }, []);

  const handleSignIn = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());
    console.log("Sign In submitted:", data);

    // Simulate successful sign in and navigate back to home
    setTimeout(() => {
      navigate('/');
    }, 1000);
  };

  const handleGoogleSignIn = () => {
    console.log("Continue with Google clicked");
    alert("Continue with Google clicked");
  };

  const handleResetPassword = () => {
    alert("Reset Password clicked");
  };

  const handleCreateAccount = () => {
    alert("Create Account clicked");
  };

  const navbarConfig = {
    logo: {
      url: "/",
      src: "",
      alt: "Fleait logo",
      title: "Fleait"
    },
    menu: [
      { title: "Home", url: "/" },
      { title: "Products", url: "/products" },
      {
        title: "Categories",
        url: "#",
        items: [
          {
            title: "Fashion & Accessories",
            description: "Luxury bags, watches, jewelry, and fashion items",
            icon: <ShoppingBag className="size-5 shrink-0" />,
            url: "#",
          },
          {
            title: "Premium Brands",
            description: "Curated selection of high-end designer brands",
            icon: <Tag className="size-5 shrink-0" />,
            url: "#",
          },
        ],
      },
      {
        title: "Services",
        url: "#",
        items: [
          {
            title: "Personal Shopping",
            description: "Get personalized recommendations from our experts",
            icon: <Users className="size-5 shrink-0" />,
            url: "#",
          },
          {
            title: "Customer Support",
            description: "24/7 support for all your luxury shopping needs",
            icon: <Headphones className="size-5 shrink-0" />,
            url: "#",
          },
        ],
      },
      {
        title: "About",
        url: "/about",
      },
      {
        title: "Contact",
        url: "#",
      },
    ],
    mobileExtraLinks: [
      { name: "Gift Cards", url: "#" },
      { name: "Size Guide", url: "#" },
      { name: "Returns", url: "#" },
      { name: "Shipping", url: "#" },
    ],
    auth: {
      login: { text: "Sign In", url: "/signin" },
      signup: { text: "Create Space", url: "#" },
    },
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="bg-white text-gray-900">
      {/* Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-6 px-4 pointer-events-none animate-navbar-float">
        <div className="max-w-7xl w-full bg-[rgb(255, 255, 255)] border-2 border-[rgb(0, 0, 0)] rounded-[40px] px-8 pointer-events-auto transition-all duration-500 hover:scale-[1.01] relative overflow-hidden before:absolute before:inset-0 before:z-[-1] before:opacity-60 before:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')]">
          <Navbar {...navbarConfig} />
        </div>
      </div>

      <SignInComponent
        title={<span className="font-logo">Welcome to Fleait</span>}
        description="Access your one stop flea market across the globe"
        heroImageSrc={heroImage}
        testimonials={sampleTestimonials}
        onSignIn={handleSignIn}
        onGoogleSignIn={handleGoogleSignIn}
        onResetPassword={handleResetPassword}
        onCreateAccount={handleCreateAccount}
        showSellerLink={true}
        onSellerLinkClick={() => navigate('/seller')}
      />
    </div>
  );
};

export default SignInDemo;